/**
 * Экран «История» (раздел 8, пункт 5): что было сделано и как это исправить.
 *
 * Два решения определяют весь файл.
 *
 * Первое: экран ничего не считает. Он берёт `GET /sets` и показывает те же
 * числа, что записал сервер (продуктовый принцип 1). Ни суммарного тоннажа,
 * ни «среднего RPE за день» здесь нет — не потому, что лень, а потому что
 * любая такая цифра на клиенте разошлась бы с движком, который считает по
 * всей истории, а не по загруженной странице.
 *
 * Второе: правка идёт НАПРЯМУЮ через `api`, а не через офлайн-очередь.
 * Очередь (`offline/queue`) существует ради подвала без связи, где человек
 * стоит между подходами и запись обязана вернуть управление мгновенно.
 * История правится дома, за столом, при работающем интернете: там честнее
 * дождаться ответа сервера и показать отказ, чем молча положить правку в
 * очередь и оставить пользователя с уверенностью, что он всё исправил.
 * К тому же очередь идемпотентна по `client_uuid` и умеет только POST /sets —
 * PATCH и DELETE в неё не укладываются.
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import { ApiError, NetworkError, api } from '../api/client'
import type { WorkoutSet } from '../api/types'
import { subscribe, type QueueState } from '../offline/queue'
import { useExercises } from '../api/exercises'
import { ExerciseHead } from '../components/ExerciseHead'
import { useAsync } from '../hooks'
import { haptic } from '../auth/telegram'
import {
  ConfirmButton,
  Empty,
  ErrorBox,
  Loader,
  Stepper,
  formatDate,
  formatWeight,
} from '../components/ui'

/**
 * Размер страницы.
 *
 * Сервер по умолчанию отдаёт 200 (`sets.DEFAULT_LIMIT`), но limit задаётся
 * явно: правило «пришло меньше limit — значит это конец» работает только
 * тогда, когда клиент знает limit точно, а не догадывается о серверном
 * умолчании. 100 подходов — это около трёх недель тренировок: хватает,
 * чтобы найти нужный день, и не грузит мобильный интернет лишним.
 */
const PAGE = 100

// --------------------------------------------------------------------------- //
// Подготовка данных к показу
// --------------------------------------------------------------------------- //

/**
 * Календарный день подхода глазами пользователя.
 *
 * `performed_at` приходит моментом в UTC, а группировать надо по тому дню,
 * который человек видел на часах: тренировка в 23:30 — это вчерашний вечер,
 * а не сегодняшнее утро. Это форматирование, а не расчёт: часовой пояс берём
 * у устройства — тот же, по которому пользователь живёт и в котором сервер
 * считает его дни.
 */
function localDayKey(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Время подхода: помогает отличить два одинаковых подхода друг от друга. */
function localTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}

interface ExerciseGroup {
  exerciseId: number
  sets: WorkoutSet[]
}

interface DayGroup {
  /** Ключ дня в виде YYYY-MM-DD — его понимает `formatDate`. */
  day: string
  exercises: ExerciseGroup[]
}

/**
 * Разложить плоский список по дням и упражнениям.
 *
 * Порядок сервера (свежие сверху) не переставляется нигде: и дни, и
 * упражнения внутри дня, и подходы внутри упражнения идут в том порядке, в
 * котором их отдал `GET /sets`. Своя сортировка тут создала бы вторую
 * версию правды при постраничной подгрузке.
 */
function groupByDay(sets: WorkoutSet[]): DayGroup[] {
  const days: DayGroup[] = []
  const byKey = new Map<string, DayGroup>()

  for (const s of sets) {
    const key = localDayKey(s.performed_at)
    let day = byKey.get(key)
    if (!day) {
      day = { day: key, exercises: [] }
      byKey.set(key, day)
      days.push(day)
    }
    let group = day.exercises.find((g) => g.exerciseId === s.exercise_id)
    if (!group) {
      group = { exerciseId: s.exercise_id, sets: [] }
      day.exercises.push(group)
    }
    group.sets.push(s)
  }
  return days
}

/** «60 кг × 8 @ RPE 8» — ровно то, что записано, без пересчётов. */
function describe(row: WorkoutSet): string {
  const rpe = row.rpe === null ? '' : ` @ RPE ${formatWeight(row.rpe)}`
  return `${formatWeight(row.weight)} кг × ${row.reps}${rpe}`
}

/**
 * Причина отказа человеческим языком. Сеть и сервер — разные беды.
 *
 * Текст про отсутствие связи задаётся вызывающим: «правка не ушла» и «список
 * не обновился» — это разные новости, и подставлять одну вместо другой значило
 * бы соврать про то, что именно не получилось.
 */
function failureText(e: unknown, offline: string): string {
  if (e instanceof NetworkError) return offline
  if (e instanceof ApiError) {
    return e.isAuth ? 'Сессия истекла — открой приложение заново.' : e.message
  }
  return 'Не удалось выполнить действие'
}

const OFFLINE_EDIT =
  'Правка не ушла: нет связи. История правится на сервере — попробуй, когда появится интернет.'
const OFFLINE_LIST = 'Список не обновился: нет связи. История хранится на сервере.'

/**
 * Заголовок дня.
 *
 * `formatDate` года не пишет — для экрана «Сегодня» это верно, а история
 * уходит на месяцы назад, где «8 сентября» без года двусмысленно. Год
 * дописывается только к календарной записи: «вчера 2025» — бессмыслица.
 */
const RELATIVE_LABELS = new Set(['сегодня', 'вчера', 'завтра'])

function dayTitle(dayKey: string, now = new Date()): string {
  const label = formatDate(dayKey, now)
  if (RELATIVE_LABELS.has(label)) return label
  const year = dayKey.slice(0, 4)
  return year === String(now.getFullYear()) ? label : `${label} ${year}`
}

// --------------------------------------------------------------------------- //
// Строка и её правка
// --------------------------------------------------------------------------- //

function SetRow({ row, onOpen }: { row: WorkoutSet; onOpen: () => void }) {
  return (
    <button
      type="button"
      className={`hist-row${row.is_warmup ? ' warm' : ''}`}
      onClick={onOpen}
    >
      <span>
        <span className="tnum">{describe(row)}</span>
        {row.is_warmup && <span className="hist-warm">разминка</span>}
      </span>
      <span className="faint tnum">{localTime(row.performed_at)}</span>
    </button>
  )
}

interface EditorProps {
  row: WorkoutSet
  onSaved: (next: WorkoutSet) => void
  onDeleted: (id: number) => void
  onCancel: () => void
}

/**
 * Правка одного подхода.
 *
 * Черновик живёт в состоянии этого компонента, а не экрана: элемент
 * пересоздаётся при переключении строки, и недоведённая правка соседнего
 * подхода не переезжает на новый.
 */
function SetEditor({ row, onSaved, onDeleted, onCancel }: EditorProps) {
  const [weight, setWeight] = useState(row.weight)
  const [reps, setReps] = useState(row.reps)
  const [rpe, setRpe] = useState<number | null>(row.rpe)
  /**
   * RPE выбран человеком, а не подставлен экраном.
   *
   * Шкале нужна точка старта, но 8 — не нейтральное число: это и цель
   * калибровочной недели (раздел 5.2), и порог, на котором double progression
   * поднимает вес (раздел 5.4). Уехав в `workout_set` молча, эта восьмёрка
   * стала бы фактом, из которого движок посчитает e1RM и следующий рабочий
   * вес, — то есть клиент придумал бы цифру за пользователя (принцип 1).
   * Поэтому подставленный старт в PATCH не уходит, пока его не подтвердили
   * кнопками шкалы.
   */
  const [rpeChosen, setRpeChosen] = useState(true)
  const [isWarmup, setIsWarmup] = useState(row.is_warmup)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<unknown>(null)

  const save = async () => {
    // В PATCH уходят только изменённые поля: сервер разбирает тело через
    // `exclude_unset`, и лишнее поле — это лишний шанс затереть то, чего
    // пользователь не трогал.
    const patch: Record<string, number | boolean | null> = {}
    // Неподтверждённый старт шкалы фактом не считается и остаётся как было.
    const nextRpe = rpeChosen ? rpe : row.rpe
    if (weight !== row.weight) patch.weight = weight
    if (reps !== row.reps) patch.reps = reps
    if (nextRpe !== row.rpe) patch.rpe = nextRpe
    if (isWarmup !== row.is_warmup) patch.is_warmup = isWarmup

    if (Object.keys(patch).length === 0) {
      onCancel()
      return
    }

    setBusy(true)
    setFailure(null)
    try {
      const updated = await api.patch<WorkoutSet>(`/sets/${row.id}`, patch)
      haptic('light')
      // Обновляем строку тем, что вернул сервер, а не своим черновиком:
      // источник правды один, и он на той стороне.
      onSaved(updated)
    } catch (e) {
      setFailure(e)
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    setFailure(null)
    try {
      await api.delete<void>(`/sets/${row.id}`)
      haptic('medium')
      onDeleted(row.id)
    } catch (e) {
      setFailure(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="hist-edit">
      <div className="faint">
        было: {describe(row)} · {localTime(row.performed_at)}
      </div>

      <Stepper
        label="Вес"
        value={weight}
        onChange={setWeight}
        step={2.5}
        // Мелкий шаг — как на тренировке: исправляют сюда ровно тот вес,
        // который не получилось записать сразу.
        fineStep={0.25}
        min={0}
        max={500}
        unit="кг"
        decimals={2}
      />
      <Stepper
        label="Повторы"
        value={reps}
        onChange={setReps}
        step={1}
        // Ноль повторов — это не подход, а его отсутствие: такую запись
        // удаляют, а не правят.
        min={1}
        max={100}
      />

      {rpe === null ? (
        // RPE в базе nullable: в зале его иногда не ставят, и «дописать
        // задним числом» — законная правка. Шкала открывается с восьмёрки
        // просто потому, что откуда-то шагать надо; записанной она станет
        // только после подтверждения — см. `rpeChosen`.
        <button
          type="button"
          className="btn ghost wide"
          onClick={() => {
            setRpe(8)
            setRpeChosen(false)
          }}
        >
          Добавить RPE
        </button>
      ) : (
        <>
          <Stepper
            label="RPE"
            value={rpe}
            onChange={(next) => {
              setRpe(next)
              setRpeChosen(true)
            }}
            step={0.5}
            min={1}
            max={10}
            decimals={1}
          />
          {!rpeChosen && (
            <div className="faint">
              RPE ещё не записан: выставь оценку кнопками — экран не проставит
              её за тебя.
            </div>
          )}
          <button
            type="button"
            className="btn ghost wide"
            onClick={() => {
              setRpe(null)
              setRpeChosen(true)
            }}
          >
            Убрать RPE
          </button>
        </>
      )}

      {/* Признак разминки правится здесь же: перепутанная разминка искажает
          рабочие веса в движке сильнее, чем ошибка в одном повторе. */}
      <button
        type="button"
        className="btn ghost wide"
        onClick={() => setIsWarmup((v) => !v)}
      >
        Разминка — {isWarmup ? 'да' : 'нет'}
      </button>

      {failure !== null && (
        <div style={{ color: 'var(--danger)', fontSize: 15 }}>
          {failureText(failure, OFFLINE_EDIT)}
        </div>
      )}

      <div className="row">
        <button
          type="button"
          className="btn primary"
          style={{ flex: 1 }}
          disabled={busy}
          onClick={() => void save()}
        >
          {busy ? 'Сохраняю…' : 'Сохранить'}
        </button>
        <button type="button" className="btn ghost" disabled={busy} onClick={onCancel}>
          Отмена
        </button>
      </div>

      {/* Пока в сети висит правка, удалять нечего: два запроса по одной
          строке разошлись бы, и последний ответ решал бы за пользователя. */}
      {busy ? (
        <button type="button" className="btn danger wide" disabled>
          Удалить подход
        </button>
      ) : (
        <ConfirmButton
          className="btn danger wide"
          confirmLabel="Точно удалить?"
          onConfirm={() => void remove()}
        >
          Удалить подход
        </ConfirmButton>
      )}
    </div>
  )
}

/** Состояние очереди синхронизации. */
function useQueue(): QueueState | null {
  const [state, setState] = useState<QueueState | null>(null)
  useEffect(() => subscribe(setState), [])
  return state
}

/**
 * Напоминание про неотправленные подходы.
 *
 * Без него экран выглядит соврамши: человек записал подход в подвале без
 * связи, открыл историю — а его там нет, потому что история живёт на
 * сервере, а подход ещё в очереди.
 *
 * «Ждут отправки» и «сервер не принял» разведены намеренно. Первое пройдёт
 * само, когда появится связь. Второе само не пройдёт никогда: запись отвергнута
 * по существу и лежит в очереди до повтора вручную. Обещать про неё «появится
 * после отправки» — та самая пустая успокоительная фраза, которую запрещает
 * принцип честности (раздел 2).
 */
function QueueNote({ pending, failed }: { pending: number; failed: number }) {
  return (
    <>
      {pending > 0 && (
        <div className="flag info">
          {pending} подх. ещё не на сервере — они появятся в истории, как только уедут.
        </div>
      )}
      {failed > 0 && (
        <div className="flag warning">
          {failed} подх. сервер не принял. В историю они не попадут, пока отправка
          не повторится — повтор предлагает полоска наверху экрана.
        </div>
      )}
    </>
  )
}

// --------------------------------------------------------------------------- //
// Экран
// --------------------------------------------------------------------------- //

export default function History() {
  /** Смещение для следующей страницы. В рендере не участвует — держим в ref. */
  const offset = useRef(0)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [moreError, setMoreError] = useState<unknown>(null)
  const [editingId, setEditingId] = useState<number | null>(null)

  const { data, error, loading, reload, set } = useAsync<WorkoutSet[]>(async () => {
    const page = await api.get<WorkoutSet[]>(`/sets?limit=${PAGE}&offset=0`)
    offset.current = page.length
    // Пришло меньше страницы — значит это вся история, кнопку не показываем.
    setHasMore(page.length === PAGE)
    setMoreError(null)
    setEditingId(null)
    return page
  }, [])

  // Полный справочник с сервера: история тянется за месяцы, а сегодняшняя
  // тренировка знает от силы пяток движений. Справочник кешируется в
  // IndexedDB, поэтому названия есть и без связи.
  const catalog = useExercises()
  const nameOf = (id: number) => catalog.name(id)
  // Раскрытая техника — одна на экран, ключ «день-движение».
  const [peek, setPeek] = useState<string | null>(null)

  const queue = useQueue()
  const pending = queue?.pending ?? 0
  const failed = queue?.failed ?? 0

  /**
   * Очередь опустела — перечитываем первую страницу.
   *
   * Без этого экран врёт: подходы, записанные без связи, уезжают на сервер
   * фоновым прогоном, плашка «ещё не на сервере» исчезает, а списка это не
   * касается — и внизу продолжает стоять «Это вся записанная история», хотя
   * только что уехавшей тренировки в ней нет.
   */
  const wasPending = useRef(pending)
  const [refreshDue, setRefreshDue] = useState(false)
  useEffect(() => {
    const before = wasPending.current
    wasPending.current = pending
    if (before > 0 && pending === 0) setRefreshDue(true)
  }, [pending])

  // Перезагрузка закрывает открытую правку, поэтому она ждёт, пока человек
  // договорит: недописанный черновик дороже пары секунд свежести.
  useEffect(() => {
    if (!refreshDue || editingId !== null) return
    setRefreshDue(false)
    void reload()
  }, [refreshDue, editingId, reload])

  const rows = data ?? []
  const days = useMemo(() => groupByDay(rows), [rows])

  const loadMore = async () => {
    if (loadingMore || !data) return
    setLoadingMore(true)
    setMoreError(null)
    try {
      const page = await api.get<WorkoutSet[]>(
        `/sets?limit=${PAGE}&offset=${offset.current}`,
      )
      // Сдвигаемся на фактически полученное, а не на PAGE: так смещение
      // остаётся верным, даже если сервер отдал неполную страницу.
      offset.current += page.length
      setHasMore(page.length === PAGE)
      set([...data, ...page])
    } catch (e) {
      setMoreError(e)
    } finally {
      setLoadingMore(false)
    }
  }

  const applyRow = (next: WorkoutSet) => {
    if (!data) return
    set(data.map((r) => (r.id === next.id ? next : r)))
    setEditingId(null)
  }

  const dropRow = (id: number) => {
    if (!data) return
    set(data.filter((r) => r.id !== id))
    setEditingId(null)
    // Запись исчезла и на сервере: если не сдвинуть смещение, следующая
    // страница начнётся на один подход дальше и один подход потеряется.
    offset.current = Math.max(0, offset.current - 1)
  }

  if (loading && data === null) {
    return (
      <div className="screen">
        <h1>История</h1>
        <Loader label="Поднимаю записи…" />
      </div>
    )
  }

  if (error !== null && data === null) {
    return (
      <div className="screen">
        <h1>История</h1>
        <ErrorBox error={error} onRetry={() => void reload()} />
        {error instanceof NetworkError && (
          <p className="faint">
            История хранится на сервере — без связи её не показать. Записывать
            подходы это не мешает: они уедут, когда связь вернётся.
          </p>
        )}
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="screen">
        <h1>История</h1>
        <QueueNote pending={pending} failed={failed} />
        {error !== null && (
          <ErrorBox error={error} onRetry={() => void reload()} />
        )}
        <Empty
          title="Записей пока нет"
          hint={
            pending + failed > 0
              ? 'На сервере пока пусто: записанные подходы ещё не уехали. Список наполнится после синхронизации.'
              : 'Подходы появляются здесь сразу после первой тренировки — начни её на экране «Сегодня».'
          }
        />
      </div>
    )
  }

  return (
    <div className="screen">
      <h1>История</h1>
      <p className="faint" style={{ margin: '0 0 8px' }}>
        Тап по подходу — исправить или удалить запись.
      </p>

      <QueueNote pending={pending} failed={failed} />

      {/* Обновление сорвалось, но список уже показан: молчать про это нельзя —
          человек будет считать увиденное свежим. */}
      {error !== null && (
        <div className="card" style={{ color: 'var(--danger)' }}>
          <div style={{ marginBottom: 12 }}>{failureText(error, OFFLINE_LIST)}</div>
          <button type="button" className="btn wide" onClick={() => void reload()}>
            Повторить
          </button>
        </div>
      )}

      {days.map((day) => (
        <section key={day.day}>
          <h2>{dayTitle(day.day)}</h2>
          {day.exercises.map((group) => {
            const peekKey = `${day.day}-${group.exerciseId}`
            return (
              <div className="card tight" key={peekKey}>
                {/* Миниатюра — отдельная кнопка в шапке группы, вне строк
                    подходов: тап по подходу открывает правку записи, и
                    промахнуться из «посмотреть» в «исправить» нельзя. Тап по
                    подходу просмотр не закрывает: он стоит выше, и его
                    закрытие унесло бы открытый редактор за верхний край. */}
                <ExerciseHead
                  exercise={{
                    id: group.exerciseId,
                    name: nameOf(group.exerciseId),
                    is_custom: catalog.byId.get(group.exerciseId)?.is_custom,
                  }}
                  open={peek === peekKey}
                  onToggle={() => setPeek((prev) => (prev === peekKey ? null : peekKey))}
                  title={<div className="hist-ex">{nameOf(group.exerciseId)}</div>}
                />
                {group.sets.map((row) =>
                  row.id === editingId ? (
                    <SetEditor
                      key={row.id}
                      row={row}
                      onSaved={applyRow}
                      onDeleted={dropRow}
                      onCancel={() => setEditingId(null)}
                    />
                  ) : (
                    <SetRow
                      key={row.id}
                      row={row}
                      onOpen={() => {
                        haptic('light')
                        setEditingId(row.id)
                      }}
                    />
                  ),
                )}
              </div>
            )
          })}
        </section>
      ))}

      {moreError !== null && (
        <div className="card" style={{ color: 'var(--danger)' }}>
          {failureText(moreError, OFFLINE_LIST)}
        </div>
      )}

      {hasMore && (
        <button
          type="button"
          className="btn wide"
          disabled={loadingMore}
          onClick={() => void loadMore()}
        >
          {loadingMore ? 'Загружаю…' : 'Показать ещё'}
        </button>
      )}

      {/* «Вся история» — утверждение про сервер. Пока в очереди лежат подходы,
          оно неверно, и произносить его нельзя. */}
      {!hasMore && (
        <p className="faint">
          {pending + failed > 0
            ? 'Это вся история, которая уже на сервере. Подходы из очереди появятся здесь после отправки.'
            : 'Это вся записанная история.'}
        </p>
      )}
    </div>
  )
}
